import Layout from "@/components/layout/Layout";
import PageHero from "@/components/shared/PageHero";
import PartnersSection from "@/components/home/PartnersSection";
import SEOHead from "@/components/seo/SEOHead";
import SchemaMarkup from "@/components/seo/SchemaMarkup";
import { MapPin, Target, Eye, Users, Server } from "lucide-react";

const datacenters = [
  { city: "Dhaka", country: "Bangladesh", lat: 23.8103, lng: 90.4125 },
  { city: "Khulna", country: "Bangladesh", lat: 22.8456, lng: 89.5403 },
  { city: "Rajshahi", country: "Bangladesh", lat: 24.3745, lng: 88.6042 },
];

const stats = [
  { value: "500+", label: "Active Websites" },
  { value: "300+", label: "Happy Clients" },
  { value: "99.99%", label: "Uptime Record" },
  { value: "2+", label: "Years Experience" },
];

const About = () => {
  return (
    <Layout>
      <SEOHead
        title="About Web Host Buy BD | Bangladesh's #1 BDIX Hosting Provider Since 2025"
        description="Learn about Web Host Buy BD - Bangladesh's leading BDIX hosting provider since 2016. 50,000+ websites hosted, 10,000+ happy clients. Best BDIX hosting in Bangladesh with datacenters in Dhaka, Khulna &amp; Rajshahi."
        canonical="/about-us"
        keywords="Web Host Buy BD, best BDIX hosting in Bangladesh, best hosting provider in Bangladesh, bdix hosting in bangladesh"
      />
      <SchemaMarkup
        type="about"
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "About", url: "/about-us" },
        ]}
        pageName="About Web Host Buy BD - Best BDIX Hosting Provider"
        pageDescription="Bangladesh's leading BDIX web hosting provider since 2025"
      />
      <PageHero
        badge="About Us"
        title="Bangladesh's Leading BDIX Hosting Provider"
        subtitle="Empowering businesses with reliable, high-performance BDIX hosting solutions since 2025. Fast BDIX hosting Bangladesh trusts."
      />

      {/* Company Overview */}
      <section className="py-20 bg-background" aria-labelledby="our-story-heading">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <article>
              <h2 id="our-story-heading" className="text-3xl font-bold text-foreground mb-6">
                Our Story
              </h2>
              <div className="space-y-4 text-muted-foreground">
                <p>
                  Web Host Buy BD was founded in 2025 with a simple mission: to provide 
                  affordable, reliable, and high-performance BDIX web hosting solutions to 
                  businesses and individuals in Bangladesh.
                </p>
                <p>
                  Today, we're proud to be one of the leading BDIX hosting providers in Bangladesh, 
                  serving over 500 websites and 300+ satisfied customers. Our commitment 
                  to quality, innovation, and customer satisfaction has made us the trusted 
                  choice for hosting needs with competitive hosting prices in BD.
                </p>
                <p>
                  With state-of-the-art datacenters strategically located across Bangladesh, 
                  we offer unparalleled BDIX connectivity, ensuring your website loads 
                  lightning-fast for local visitors. All our web hosting, VPS, and dedicated 
                  servers are BDIX-connected for optimal performance.
                </p>
              </div>
            </article>

            <div className="grid grid-cols-2 gap-4">
              {stats.map((stat) => (
                <div
                  key={stat.label}
                  className="p-6 rounded-xl bg-card border border-border text-center"
                >
                  <div className="text-3xl font-bold text-primary mb-2">
                    {stat.value}
                  </div>
                  <div className="text-sm text-muted-foreground">{stat.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 bg-muted/30" aria-labelledby="mission-vision-heading">
        <div className="container-custom">
          <h2 id="mission-vision-heading" className="sr-only">Our Mission and Vision</h2>
          <div className="grid md:grid-cols-2 gap-8">
            <article className="p-8 rounded-2xl bg-card border border-border text-center md:text-left">
              <div className="w-14 h-14 gradient-primary rounded-xl flex items-center justify-center mb-6 mx-auto md:mx-0" aria-hidden="true">
                <Target className="w-7 h-7 text-primary-foreground" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Our Mission</h3>
              <p className="text-muted-foreground leading-relaxed">
                To empower businesses and individuals in Bangladesh with world-class 
                BDIX hosting infrastructure, exceptional customer support, and innovative 
                solutions that drive digital success with affordable hosting prices in BD.
              </p>
            </article>

            <article className="p-8 rounded-2xl bg-card border border-border text-center md:text-left">
              <div className="w-14 h-14 gradient-primary rounded-xl flex items-center justify-center mb-6 mx-auto md:mx-0" aria-hidden="true">
                <Eye className="w-7 h-7 text-primary-foreground" />
              </div>
              <h3 className="text-2xl font-bold text-foreground mb-4">Our Vision</h3>
              <p className="text-muted-foreground leading-relaxed">
                To be the most trusted and preferred BDIX hosting provider in South Asia, 
                known for reliability, innovation, and unwavering commitment to 
                customer success in Bangladesh web hosting.
              </p>
            </article>
          </div>
        </div>
      </section>

      {/* Datacenter Locations */}
      <section className="py-20 bg-background" aria-labelledby="datacenter-heading">
        <div className="container-custom">
          <header className="text-center mb-12">
            <span className="inline-block px-4 py-1.5 rounded-full bg-blue-50 text-primary text-sm font-medium mb-4">
              BDIX Infrastructure
            </span>
            <h2 id="datacenter-heading" className="text-3xl font-bold text-foreground mb-4">
              Bangladesh Datacenter Locations
            </h2>
            <p className="text-muted-foreground max-w-2xl mx-auto">
              Strategically located BDIX datacenters across Bangladesh for optimal performance and redundancy.
            </p>
          </header>

          <div className="grid md:grid-cols-3 gap-6 mb-12">
            {datacenters.map((dc) => (
              <article
                key={dc.city}
                className="p-6 rounded-xl bg-card border border-border text-center card-hover"
              >
                <div className="w-12 h-12 mx-auto gradient-primary rounded-full flex items-center justify-center mb-4" aria-hidden="true">
                  <MapPin className="w-6 h-6 text-primary-foreground" />
                </div>
                <h3 className="text-xl font-semibold text-foreground mb-1">
                  {dc.city}
                </h3>
                <p className="text-muted-foreground text-sm">{dc.country}</p>
              </article>
            ))}
          </div>

          {/* Map */}
          <div className="rounded-2xl overflow-hidden border border-border">
            <iframe
              src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3651.902305426863!2d90.39!3d23.78!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x0%3A0x0!2zMjPCsDQ2JzQ4LjAiTiA5MMKwMjMnMjQuMCJF!5e0!3m2!1sen!2sbd!4v1234567890"
              width="100%"
              height="400"
              style={{ border: 0 }}
              allowFullScreen
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
              title="Web Host Buy BD BDIX Datacenter Locations in Bangladesh"
            />
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-muted/30" aria-labelledby="why-choose-heading">
        <div className="container-custom">
          <header className="text-center mb-12">
            <h2 id="why-choose-heading" className="text-3xl font-bold text-foreground mb-4">
              Why Choose Web Host Buy BD?
            </h2>
          </header>

          <div className="grid md:grid-cols-3 gap-6">
            <article className="p-6 rounded-xl bg-card border border-border text-center md:text-left">
              <div className="feature-icon mb-4 mx-auto md:mx-0" aria-hidden="true">
                <Server className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Enterprise BDIX Infrastructure
              </h3>
              <p className="text-muted-foreground text-sm">
                State-of-the-art hardware with redundant power, cooling, and BDIX network connectivity for fast BDIX hosting Bangladesh.
              </p>
            </article>

            <article className="p-6 rounded-xl bg-card border border-border text-center md:text-left">
              <div className="feature-icon mb-4 mx-auto md:mx-0" aria-hidden="true">
                <Users className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                Local Expert Support
              </h3>
              <p className="text-muted-foreground text-sm">
                Our team of certified professionals is available 24/7 to assist you in Bangla or English with affordable hosting price in BD.
              </p>
            </article>

            <article className="p-6 rounded-xl bg-card border border-border text-center md:text-left">
              <div className="feature-icon mb-4 mx-auto md:mx-0" aria-hidden="true">
                <MapPin className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-semibold text-foreground mb-2">
                BDIX Connectivity
              </h3>
              <p className="text-muted-foreground text-sm">
                Direct peering with BDIX ensures lightning-fast speeds for your Bangladeshi visitors. All hosting plans are BDIX-connected.
              </p>
            </article>
          </div>
        </div>
      </section>

      <PartnersSection />
    </Layout>
  );
};

export default About;